import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { SocialLoginButton } from '@/components/auth'
import { useAuthStore } from '@/store/authStore'
import { useAnalysisStore } from '@/store/analysisStore'
import { getCurrentUser } from '@/lib/firebase'
import { trackFunnelEvent, trackError } from '@/services/analytics'
import type { AuthProvider } from '@/types/auth'

// Check if there was a recent redirect (for showing loading state)
// Must match the timeout in firebase.ts checkAndClearPendingRedirect (5 minutes)
function checkRecentRedirect(): boolean {
  try {
    const pending = localStorage.getItem('k-skin-pending-redirect')
    if (pending) {
      const timestamp = parseInt(pending, 10)
      // Consider redirect "recent" if within 5 minutes (matches firebase.ts)
      return Date.now() - timestamp < 5 * 60 * 1000
    }
    return false
  } catch {
    return false
  }
}

// Decorative sparkle with animation
const Sparkle = ({
  className,
  size = 24,
  color = '#FF8C33',
  delay = 0
}: {
  className?: string
  size?: number
  color?: string
  delay?: number
}) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    initial={{ scale: 0, opacity: 0, rotate: -30 }}
    animate={{
      scale: [0, 1.3, 1],
      opacity: [0, 1, 0.8],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 0.6,
      delay: delay,
      ease: "easeOut"
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

// Fun lock icon that unlocks
const UnlockIcon = () => (
  <motion.svg
    width="80"
    height="100"
    viewBox="0 0 80 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    initial={{ scale: 0.5, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.5, delay: 0.2, type: "spring", stiffness: 200 }}
  >
    {/* Glow effect */}
    <motion.ellipse
      cx="40"
      cy="60"
      rx="35"
      ry="40"
      fill="url(#lockGlow)"
      initial={{ opacity: 0 }}
      animate={{ opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
    />
    {/* Lock shackle - animated to show unlocking */}
    <motion.path
      d="M25 40 L25 28 Q25 15 40 15 Q55 15 55 28 L55 35"
      stroke="#46C0C6"
      strokeWidth="6"
      strokeLinecap="round"
      fill="none"
      initial={{ rotate: 0 }}
      animate={{ rotate: [-5, 5, -5] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
    />
    {/* Lock body */}
    <rect x="18" y="40" width="44" height="40" rx="8" fill="#46C0C6" />
    {/* Happy face - eyes */}
    <motion.g
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 0.5, type: "spring" }}
    >
      <circle cx="32" cy="55" r="3" fill="white" />
      <circle cx="48" cy="55" r="3" fill="white" />
      {/* Sparkle eyes */}
      <circle cx="33" cy="54" r="1" fill="#46C0C6" />
      <circle cx="49" cy="54" r="1" fill="#46C0C6" />
    </motion.g>
    {/* Happy face - smile */}
    <motion.path
      d="M32 66 Q40 74 48 66"
      stroke="white"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ delay: 0.6, duration: 0.4 }}
    />
    {/* Cheek blush */}
    <circle cx="26" cy="62" r="3" fill="#FFB8B8" opacity="0.6" />
    <circle cx="54" cy="62" r="3" fill="#FFB8B8" opacity="0.6" />
    {/* Gradient definition */}
    <defs>
      <radialGradient id="lockGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#46C0C6" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#46C0C6" stopOpacity="0" />
      </radialGradient>
    </defs>
  </motion.svg>
)

// Floating bubble component
const FloatingBubble = ({
  size,
  color,
  left,
  delay
}: {
  size: number
  color: string
  left: string
  delay: number
}) => (
  <motion.div
    className="absolute rounded-full"
    style={{
      width: size,
      height: size,
      backgroundColor: color,
      left,
      bottom: -20
    }}
    animate={{
      y: [0, -800],
      opacity: [0, 0.6, 0.6, 0]
    }}
    transition={{
      duration: 8 + Math.random() * 4,
      delay,
      repeat: Infinity,
      ease: "easeInOut"
    }}
  />
)

// Detect Android
const isAndroid = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent)

export function LoginPage() {
  const navigate = useNavigate()
  const { signIn, isLoading, error, isAuthenticated, user, hasPaid, demoMode, setDemoMode } = useAuthStore()
  const { analysisResults } = useAnalysisStore()
  const [loadingProvider, setLoadingProvider] = useState<AuthProvider | null>(null)

  // Check if we're waiting for redirect auth to complete
  const [waitingForRedirect, setWaitingForRedirect] = useState(() => checkRecentRedirect())

  // Clear waiting state after timeout (if auth doesn't come through)
  useEffect(() => {
    if (waitingForRedirect) {
      console.log('[LoginPage] Waiting for redirect auth...')

      // Poll for Firebase auth state while waiting
      const pollInterval = setInterval(() => {
        const firebaseUser = getCurrentUser()
        console.log('[LoginPage] Polling Firebase auth, user:', firebaseUser?.email || 'null')
        if (firebaseUser) {
          console.log('[LoginPage] Firebase user found, auth should update soon')
        }
      }, 2000)

      const timeout = setTimeout(() => {
        console.log('[LoginPage] Redirect timeout - showing login buttons')
        setWaitingForRedirect(false)
        localStorage.removeItem('k-skin-pending-redirect')
        clearInterval(pollInterval)
      }, 15000) // Wait up to 15 seconds for redirect auth

      return () => {
        clearTimeout(timeout)
        clearInterval(pollInterval)
      }
    }
  }, [waitingForRedirect])

  // Route guard: check teaser flow completion and auth status
  // IMPORTANT: Skip route guards while waiting for redirect auth to complete
  const hasRedirected = useRef(false)
  useEffect(() => {
    if (hasRedirected.current) return

    // Don't run route guards while waiting for redirect auth
    if (waitingForRedirect) {
      console.log('[LoginPage] Skipping route guard - waiting for redirect auth')
      return
    }

    // Must have completed teaser flow (survey + photo)
    if (!analysisResults) {
      hasRedirected.current = true
      navigate('/survey', { replace: true })
      return
    }

    // If already paid, go to report
    if ((hasPaid || demoMode) && analysisResults) {
      hasRedirected.current = true
      navigate('/report', { state: { showWelcomeToast: true } })
      return
    }

    // If authenticated with a valid user, navigate to payment
    // Trust Zustand state - authStore.initAuth handles stale state clearing properly
    if (isAuthenticated && user) {
      console.log('[LoginPage] User is authenticated, navigating to payment')
      hasRedirected.current = true
      setWaitingForRedirect(false)
      localStorage.removeItem('k-skin-pending-redirect')
      navigate('/payment')
    }
  }, [isAuthenticated, user, hasPaid, demoMode, analysisResults, navigate, waitingForRedirect])

  const handleSocialLogin = async (provider: AuthProvider) => {
    setLoadingProvider(provider)
    trackFunnelEvent.loginStarted(provider)
    try {
      const success = await signIn(provider)
      if (success) {
        trackFunnelEvent.loginCompleted(provider)
        navigate('/payment')
      }
    } catch (err) {
      console.error('[Login] Login failed:', err)
      const errorCode = (err as { code?: string })?.code || 'unknown'
      trackFunnelEvent.loginFailed(errorCode)
      trackError.auth(errorCode)
    } finally {
      setLoadingProvider(null)
    }
  }

  const handleTestPass = () => {
    setDemoMode(true)
    // Small delay to ensure state is updated before navigation
    // Go to payment page so user can see the UI (test pass button there will skip to full-survey)
    setTimeout(() => navigate('/payment'), 0)
  }

  // Bubble colors
  const bubbles = [
    { size: 20, color: '#FFB6C1', left: '10%', delay: 0 },
    { size: 15, color: '#87CEEB', left: '25%', delay: 2 },
    { size: 25, color: '#98FB98', left: '40%', delay: 1 },
    { size: 18, color: '#DDA0DD', left: '60%', delay: 3 },
    { size: 22, color: '#F0E68C', left: '75%', delay: 1.5 },
    { size: 16, color: '#E0FFFF', left: '90%', delay: 2.5 },
  ]

  return (
    <div className="min-h-dvh flex flex-col overflow-hidden relative">
      {/* CSS for animated gradient */}
      <style>{`
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .animated-gradient-bg {
          background: linear-gradient(-45deg, #FFFEF1, #F0FAFA, #FFF5F0, #E8F7F8, #FFFEF1);
          background-size: 400% 400%;
          animation: gradient-shift 10s ease infinite;
        }
      `}</style>

      {/* Animated gradient background */}
      <div className="absolute inset-0 animated-gradient-bg" />

      {/* Floating bubbles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {bubbles.map((bubble, i) => (
          <FloatingBubble key={i} {...bubble} />
        ))}
      </div>

      {/* Decorative sparkles */}
      <Sparkle className="absolute top-20 left-6 z-10" size={28} color="#FF8C33" delay={0.3} />
      <Sparkle className="absolute top-32 right-8 z-10" size={22} color="#46C0C6" delay={0.5} />
      <Sparkle className="absolute top-48 left-16 z-10" size={18} color="#E8A87C" delay={0.7} />
      <Sparkle className="absolute bottom-32 right-12 z-10" size={24} color="#7EC8E3" delay={0.4} />
      <Sparkle className="absolute bottom-48 left-10 z-10" size={20} color="#4CD964" delay={0.6} />

      {/* Header with back button */}
      <header className="relative z-10 py-4 px-4">
        <div className="flex items-center justify-between">
          {/* Back button */}
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => navigate('/teaser')}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/50 backdrop-blur-sm text-gray-600 hover:bg-white/80 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </motion.button>

          {/* Logo - bigger */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/"
              className="text-[24px]"
              style={{ fontFamily: "'Oleo Script', cursive" }}
            >
              <span className="text-[#688192]">K-Beauty</span>
              <span className="text-[#46C0C6]"> AI</span>
            </Link>
          </motion.div>

          {/* Spacer for centering */}
          <div className="w-10" />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          {/* Fun Icon */}
          <div className="flex justify-center mb-4">
            <UnlockIcon />
          </div>

          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-center mb-6"
          >
            <h1
              className="text-[24px] text-black mb-2"
              style={{ fontFamily: "'Anybody', sans-serif", fontWeight: 600 }}
            >
              <span className="text-[#46C0C6]">Almost there!</span>
            </h1>
            <p className="text-[#688192] text-[15px] leading-relaxed">
              Sign in to save your analysis
              <br />
              and unlock your full report
            </p>
          </motion.div>

          {/* Error message */}
          {error && !waitingForRedirect && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl text-red-700 text-sm text-center"
            >
              {error}
            </motion.div>
          )}

          {/* Loading state while waiting for redirect auth */}
          {waitingForRedirect ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-8 space-y-4"
            >
              <div className="w-12 h-12 border-3 border-[#46C0C6] border-t-transparent rounded-full animate-spin" />
              <p className="text-[#688192] text-sm">Completing sign in...</p>
            </motion.div>
          ) : (
            /* Social login buttons */
            <div className="space-y-3 mb-6">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <SocialLoginButton
                  provider="google"
                  onClick={() => handleSocialLogin('google')}
                  isLoading={loadingProvider === 'google'}
                  disabled={isLoading}
                />
              </motion.div>
              {/* Hide Apple login on Android - Apple Sign In doesn't work well on Android */}
              {!isAndroid && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <SocialLoginButton
                    provider="apple"
                    onClick={() => handleSocialLogin('apple')}
                    isLoading={loadingProvider === 'apple'}
                    disabled={isLoading}
                  />
                </motion.div>
              )}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <button
                  onClick={handleTestPass}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-2xl border-2 border-dashed border-[#46C0C6]/50 bg-[#46C0C6]/10 text-[#46C0C6] font-medium hover:bg-[#46C0C6]/20 transition-colors"
                >
                  <span className="text-xl">🧪</span>
                  Continue with Test Pass
                </button>
              </motion.div>
            </div>
          )}

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center space-y-2 mb-6"
          >
            <div className="flex items-center justify-center gap-2 text-[#688192]">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <span className="text-xs">Secure sign-in with 256-bit encryption</span>
            </div>
            <p className="text-xs text-[#A0A0A0]">
              No spam. Your data is used only to save your report.
            </p>
          </motion.div>

        </motion.div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-4 text-center">
        <p className="text-xs text-[#A0A0A0]">
          By signing in, you agree to our{' '}
          <Link to="/terms" className="underline hover:text-[#688192]">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="underline hover:text-[#688192]">
            Privacy Policy
          </Link>
        </p>
      </footer>
    </div>
  )
}
